import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import SEO from '../SEO';

// Import icons
import { 
  ChartBarIcon, 
  RocketLaunchIcon, 
  LightBulbIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

// Schema for audit form
interface AuditFormData {
  businessName: string;
  industry: string;
  email: string;
  currentAIUse: string;
  businessGoals: string[];
  painPoints: string;           // New field
  competitors: string;          // New field
  teamSize: string;             // New field
  techStack: string[];          // New field
  implementationPriority: string; // New field
}

export default function AuditLanding() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AuditFormData>({
    businessName: '',
    industry: '',
    email: '',
    currentAIUse: 'none',
    businessGoals: [],
    painPoints: '',           // Initialize new field
    competitors: '',          // Initialize new field
    teamSize: '',             // Initialize new field
    techStack: [],            // Initialize new field
    implementationPriority: 'medium' // Initialize new field
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStep, setFormStep] = useState(0);
  const totalSteps = 3;

  // Form progress percentage
  const formProgress = ((formStep + 1) / totalSteps) * 100;

  // Refs for animation
  const [heroRef, heroInView] = useInView({ 
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [benefitsRef, benefitsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [formRef, formInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => {
      if (name === 'techStack') {
        if (checked) {
          return {
            ...prev,
            techStack: [...prev.techStack, value]
          };
        } else {
          return {
            ...prev,
            techStack: prev.techStack.filter(tech => tech !== value)
          };
        }
      } else if (name === 'businessGoals') {
        if (checked) {
          return {
            ...prev,
            businessGoals: [...prev.businessGoals, value]
          };
        } else {
          return {
            ...prev,
            businessGoals: prev.businessGoals.filter(goal => goal !== value)
          };
        }
      }
      return prev;
    });
  };

  // Go to next step if current step is valid
  const goToNextStep = () => {
    if (validateCurrentStep()) {
      setFormStep(prev => Math.min(prev + 1, totalSteps - 1));
      window.scrollTo({
        top: document.getElementById('audit-form').offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  // Go to previous step
  const goToPreviousStep = () => {
    setFormStep(prev => Math.max(prev - 1, 0));
  };

  // Validate only fields in the current step
  const validateCurrentStep = () => {
    let formErrors = {};
    
    if (formStep === 0) {
      if (!formData.businessName.trim()) {
        formErrors['businessName'] = 'Business name is required';
      }
      
      if (!formData.industry.trim()) {
        formErrors['industry'] = 'Industry is required';
      }
      
      if (!formData.email.trim()) {
        formErrors['email'] = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        formErrors['email'] = 'Email is invalid';
      }
    } else if (formStep === 1) {
      if (formData.businessGoals.length === 0) {
        formErrors['businessGoals'] = 'Please select at least one business goal';
      }
      
      if (!formData.teamSize) {
        formErrors['teamSize'] = 'Please select your team size';
      }
    }
    
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // Handle final submission
  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateCurrentStep()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const lambdaEndpoint = "https://5f890dqtgk.execute-api.eu-north-1.amazonaws.com/audit";
      
      console.log('Submitting audit data to Lambda:', formData);
      
      const response = await fetch(lambdaEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Lambda response:', result);
      
      // The body field is a JSON string that needs to be parsed
      const resultData = result;
      
      // Store both form data and AI insights
      sessionStorage.setItem('auditData', JSON.stringify({
        ...formData,
        aiInsights: resultData.insights
      }));
      
      // Navigate to results page
      navigate('/audit-results');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error processing your audit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Custom Radio Button Component
  const CustomRadio = ({ id, name, value, checked, onChange, label }) => (
    <label 
      htmlFor={id} 
      className={`flex items-center p-4 rounded-xl transition-all cursor-pointer border ${
        checked 
          ? 'border-[#FF7066] bg-[#FF7066]/5 shadow-md' 
          : 'border-gray-200 hover:border-[#FF7066]/30'
      }`}
    >
      <input
        type="radio"
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
        checked ? 'border-[#FF7066]' : 'border-gray-300'
      }`}>
        {checked && <div className="w-3 h-3 rounded-full bg-[#FF7066]"></div>}
      </div>
      <span>{label}</span>
    </label>
  );

  // Custom Checkbox Component
  const CustomCheckbox = ({ id, name, value, checked, onChange, label }) => (
    <label 
      htmlFor={id} 
      className={`flex items-center p-4 rounded-xl transition-all cursor-pointer border ${
        checked 
          ? 'border-[#FF7066] bg-[#FF7066]/5 shadow-md' 
          : 'border-gray-200 hover:border-[#FF7066]/30'
      }`}
    >
      <input
        type="checkbox"
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <div className={`w-5 h-5 rounded md flex items-center justify-center ${
        checked ? 'bg-[#FF7066] border-[#FF7066]' : 'border-2 border-gray-300'
      }`}>
        {checked && (
          <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      <span className="ml-3">{label}</span>
    </label>
  );

  // SEO schema
  const auditSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Free AI Business Audit",
    "provider": {
      "@type": "Organization",
      "name": "BetterByAI",
      "url": "https://betterbyai.com",
      "logo": "https://betterbyai.com/logo.png"
    },
    "description": "Get a free audit to discover how AI can boost your business efficiency and revenue.",
    "serviceType": "Business Analysis",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock"
    },
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": "Business owners, executives, managers, and operations leaders"
    },
    "serviceOutput": "Personalized AI implementation strategy and recommendations",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://betterbyai.com/free-ai-audit"
    },
    "isRelatedTo": ["AI implementation", "Business automation", "Process optimization", "Generative AI"],
    "keywords": "AI business audit, AI implementation, business automation, AI strategy, business efficiency",
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How can AI help improve my business operations?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "AI can automate repetitive tasks, analyze large data sets to uncover insights, enhance customer experiences through personalization, optimize resource allocation, and help make more informed strategic decisions."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it take to implement AI in my business?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Implementation timelines vary based on complexity. Quick wins can be achieved within 1-3 months, while more complex transformations may take 3-6 months. Our business audit helps identify the right pace for your specific needs."
          }
        },
        {
          "@type": "Question",
          "name": "What is included in the free AI business audit?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our free AI business audit includes an assessment of your current operations, identification of key AI opportunities tailored to your industry, basic implementation recommendations, and potential ROI projections for AI initiatives in your business."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need technical expertise to implement AI solutions?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No, many modern AI solutions are designed to be user-friendly and require minimal technical expertise. Our audit identifies solutions appropriate for your team's capabilities, and we can recommend implementation partners if needed."
          }
        },
        {
          "@type": "Question",
          "name": "What ROI can businesses expect from AI implementation?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Businesses typically see 15-40% efficiency improvements, 20-30% cost reductions in optimized areas, and significant enhancements in data-driven decision making. Our audit provides specific ROI projections for your business."
          }
        }
      ]
    }
  };

  // Form step titles
  const stepTitles = [
    "Business Information",
    "Goals & Objectives",
    "Implementation Details"
  ];

  return (
    <>
      <SEO 
        title="Free AI Business Audit | Discover ROI Opportunities | BetterByAI"
        description="Get your personalised AI business audit and reveal hidden efficiency opportunities. Boost revenue by 20-40% through strategic AI implementation within 2 minutes."
        keywords="free AI business audit, AI assessment, business transformation, AI opportunities, ROI analysis, efficiency improvement, AI implementation strategy, business automation"
        canonical="https://betterbyai.com/free-ai-audit"
        schemaMarkup={auditSchema}
        speakable={true}
      />
    
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="pt-32 pb-16 bg-gradient-to-b from-white via-[#FF7066]/5 to-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={heroInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7066]/10 to-secondary/10 text-[#FF7066] text-sm font-medium border border-[#FF7066]/10 mb-6"
            >
              Limited Time Offer - Free AI Business Audit
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-primary mb-6"
            >
              Discover How <span className="bg-gradient-to-r from-[#FF7066] to-secondary bg-clip-text text-transparent">AI Can Transform</span> Your Business Operations
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-secondary mb-8"
            >
              Unlock hidden efficiency and revenue opportunities with our AI business audit. Get personalized recommendations in minutes.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <a 
                href="#audit-form" 
                className="inline-flex items-center bg-[#FF7066] text-white px-8 py-4 rounded-lg hover:bg-[#FF8A82] transition-colors shadow-lg shadow-[#FF7066]/20 text-lg font-medium"
              >
                Start Your Free AI Audit Now
                <ArrowRightIcon className="ml-2 w-5 h-5" />
              </a>
              <p className="text-sm text-gray-500 mt-3">
                <ClockIcon className="inline-block w-4 h-4 mr-1" />
                Takes only 2 minutes to complete
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section 
        ref={benefitsRef}
        className="py-16 bg-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-primary mb-6">
                Join Forward-Thinking Businesses Using AI To Gain A Competitive Edge
              </h2>
              <p className="text-lg text-secondary">
                Our AI audit helps businesses like yours identify key opportunities in:
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: ChartBarIcon,
                  title: "Process Automation",
                  description: "Identify tasks that can be automated to reduce costs by up to 40% and free up valuable staff time."
                },
                {
                  icon: LightBulbIcon,
                  title: "Customer Experience",
                  description: "Discover AI-driven opportunities to provide personalized customer experiences that increase retention."
                },
                {
                  icon: RocketLaunchIcon,
                  title: "Data Insights",
                  description: "Uncover hidden patterns in your business data to make better strategic decisions and stay ahead of competitors."
                }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                  className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="p-2 w-fit rounded-lg bg-[#FF7066]/10 mb-4">
                    <benefit.icon className="w-8 h-8 text-[#FF7066]" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-primary">
                    {benefit.title}
                  </h3>
                  <p className="text-secondary">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
            
            <div className="mt-12 space-y-8">
              {/* Industry Statistics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="bg-gray-50 rounded-xl p-8 border border-gray-100"
              >
                <h3 className="text-xl font-bold text-primary mb-6 text-center">
                  The Economic Impact of AI on Business
                </h3>
                
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    {
                      icon: ArrowTrendingUpIcon,
                      stat: "£2.6-4.4T",
                      text: "Annual value generative AI could add across industries"
                    },
                    {
                      icon: ChartBarIcon,
                      stat: "60-70%",
                      text: "Of employee activities that could potentially be automated with AI"
                    },
                    {
                      icon: ClockIcon,
                      stat: "0.1-0.6%",
                      text: "Potential annual labor productivity growth acceleration through 2040"
                    }
                  ].map((item, index) => (
                    <div key={index} className="text-center">
                      <div className="p-3 bg-[#FF7066]/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-8 h-8 text-[#FF7066]" />
                      </div>
                      <p className="text-3xl font-bold text-primary mb-2">{item.stat}</p>
                      <p className="text-secondary text-sm">{item.text}</p>
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-gray-500 mt-4 text-center">
                  Source: {" "}
                  <a 
                    href="https://www.mckinsey.com/capabilities/mckinsey-digital/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier#business-value"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FF7066] hover:underline"
                  >
                    McKinsey & Company, "The Economic Potential of Generative AI: The Next Productivity Frontier," 2023
                  </a>
                </p>
              </motion.div>
              
              {/* How the Audit Works */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="bg-gray-50 rounded-xl p-8 border border-gray-100"
              >
                <h3 className="text-xl font-bold text-primary mb-6 text-center">
                  How Your Free AI Audit Works
                </h3>
                
                <div className="grid md:grid-cols-4 gap-4">
                  {[
                    {
                      step: "1",
                      title: "Complete the Form",
                      description: "Answer a few questions about your business needs"
                    },
                    {
                      step: "2",
                      title: "Instant Analysis",
                      description: "Our AI analyses your specific industry & challenges"
                    },
                    {
                      step: "3",
                      title: "Get Results",
                      description: "Review your personalised opportunities report"
                    },
                    {
                      step: "4",
                      title: "Take Action",
                      description: "Implement insights or upgrade for detailed guidance"
                    }
                  ].map((item, index) => (
                    <div key={index} className="text-center relative">
                      {index < 3 && (
                        <div className="hidden md:block absolute top-8 right-0 w-full h-0.5 bg-gray-200 z-0"></div>
                      )}
                      <div className="p-3 bg-[#FF7066] rounded-full w-10 h-10 flex items-center justify-center mx-auto mb-3 relative z-10 text-white font-bold">
                        {item.step}
                      </div>
                      <h4 className="font-bold text-primary mb-1">{item.title}</h4>
                      <p className="text-secondary text-sm">{item.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
              
              {/* Trust Indicators */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="bg-gray-50 rounded-xl p-8 border border-gray-100"
              >
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div className="mb-6 md:mb-0 md:mr-8 md:w-1/3">
                    <ShieldCheckIcon className="w-16 h-16 text-[#FF7066] mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-primary text-center">
                      Your Data is Secure
                    </h3>
                  </div>
                  <div className="md:w-2/3">
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-[#FF7066] mr-3 flex-shrink-0 mt-0.5" />
                        <p className="text-secondary">
                          <strong>Enterprise-Grade Security:</strong> All data is encrypted and protected using industry-standard security protocols
                        </p>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-[#FF7066] mr-3 flex-shrink-0 mt-0.5" />
                        <p className="text-secondary">
                          <strong>GDPR Compliant:</strong> We comply with all data protection regulations and never share your information with third parties
                        </p>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-[#FF7066] mr-3 flex-shrink-0 mt-0.5" />
                        <p className="text-secondary">
                          <strong>Confidentiality Guaranteed:</strong> Your business information and audit results remain 100% confidential
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Form Section */}
      <section 
        id="audit-form"
        ref={formRef} 
        className="py-16 bg-gradient-to-b from-white via-[#FF7066]/5 to-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7066]/10 to-secondary/10 text-[#FF7066] text-sm font-medium border border-[#FF7066]/10 mb-6">
                <CheckCircleIcon className="w-5 h-5 mr-2" /> Limited Free Audits Available
              </div>
              <h2 className="text-3xl font-bold text-primary mb-4">
                Start Your Free AI Business Audit
              </h2>
              <p className="text-lg text-secondary">
                Complete the form below to receive your personalised AI opportunity assessment
              </p>
            </motion.div>
            
            {/* Form Container */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8"
            >
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between mb-2">
                  {stepTitles.map((title, idx) => (
                    <div 
                      key={idx} 
                      className={`text-sm font-medium ${formStep >= idx ? 'text-[#FF7066]' : 'text-gray-400'}`}
                      style={{ width: `${100/stepTitles.length}%`, textAlign: 'center' }}
                    >
                      Step {idx + 1}
                    </div>
                  ))}
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#FF7066] to-secondary"
                    initial={{ width: '0%' }}
                    animate={{ width: `${formProgress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                <div className="flex justify-between mt-2">
                  {stepTitles.map((title, idx) => (
                    <div 
                      key={idx} 
                      className={`text-xs ${formStep >= idx ? 'text-[#FF7066]' : 'text-gray-400'}`}
                      style={{ width: `${100/stepTitles.length}%`, textAlign: 'center' }}
                    >
                      {title}
                    </div>
                  ))}
                </div>
              </div>
              
              <form>
                <AnimatePresence mode="wait">
                  {/* Step 1: Basic Information */}
                  {formStep === 0 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h3 className="text-xl font-bold text-primary mb-6">Tell us about your business</h3>
                      
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label className="block text-gray-700 mb-2" htmlFor="businessName">
                            Business Name*
                          </label>
                          <input
                            type="text"
                            id="businessName"
                            name="businessName"
                            value={formData.businessName}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-lg border ${errors['businessName'] ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-[#FF7066] transition-all`}
                            placeholder="Your Company Ltd"
                          />
                          {errors['businessName'] && (
                            <p className="text-red-500 text-sm mt-1">{errors['businessName']}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-gray-700 mb-2" htmlFor="industry">
                            Industry*
                          </label>
                          <div className="relative">
                            <select
                              id="industry"
                              name="industry"
                              value={formData.industry}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 rounded-lg border appearance-none ${errors['industry'] ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-[#FF7066] transition-all`}
                            >
                              <option value="">Select your industry</option>
                              <option value="retail">Retail & E-commerce</option>
                              <option value="finance">Finance & Banking</option>
                              <option value="healthcare">Healthcare</option>
                              <option value="manufacturing">Manufacturing</option>
                              <option value="technology">Technology</option>
                              <option value="education">Education</option>
                              <option value="professional">Professional Services</option>
                              <option value="hospitality">Hospitality & Tourism</option>
                              <option value="other">Other</option>
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                              </svg>
                            </div>
                          </div>
                          {errors['industry'] && (
                            <p className="text-red-500 text-sm mt-1">{errors['industry']}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mb-6">
                        <label className="block text-gray-700 mb-2" htmlFor="email">
                          Business Email*
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-lg border ${errors['email'] ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-[#FF7066] transition-all`}
                          placeholder="you@yourcompany.com"
                        />
                        {errors['email'] && (
                          <p className="text-red-500 text-sm mt-1">{errors['email']}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 mb-2">
                          Current AI Implementation*
                        </label>
                        <div className="grid md:grid-cols-3 gap-4">
                          {[
                            { id: 'none', label: 'Not using AI yet' },
                            { id: 'basic', label: 'Basic AI tools only' },
                            { id: 'moderate', label: 'Moderate AI implementation' }
                          ].map((option) => (
                            <CustomRadio
                              key={option.id}
                              id={option.id}
                              name="currentAIUse"
                              value={option.id}
                              checked={formData.currentAIUse === option.id}
                              onChange={handleChange}
                              label={option.label}
                            />
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex justify-end mt-8">
                        <button
                          type="button"
                          onClick={goToNextStep}
                          className="flex items-center bg-[#FF7066] text-white px-6 py-3 rounded-lg hover:bg-[#FF8A82] transition-colors"
                        >
                          Next Step
                          <ArrowRightIcon className="ml-2 w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Step 2: Business Goals */}
                  {formStep === 1 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h3 className="text-xl font-bold text-primary mb-6">Your Business Goals</h3>
                      
                      <div className="mb-8">
                        <label className="block text-gray-700 mb-4">
                          Business Goals (Select all that apply)*
                        </label>
                        <div className="grid md:grid-cols-2 gap-3">
                          {[
                            { id: 'efficiency', label: 'Improve operational efficiency' },
                            { id: 'costs', label: 'Reduce operational costs' },
                            { id: 'cx', label: 'Enhance customer experience' },
                            { id: 'insights', label: 'Better data insights' },
                            { id: 'innovation', label: 'Drive innovation' },
                            { id: 'growth', label: 'Accelerate business growth' }
                          ].map((option) => (
                            <CustomCheckbox
                              key={option.id}
                              id={option.id}
                              name="businessGoals"
                              value={option.id}
                              checked={formData.businessGoals.includes(option.id)}
                              onChange={handleCheckboxChange}
                              label={option.label}
                            />
                          ))}
                        </div>
                        {errors['businessGoals'] && (
                          <p className="text-red-500 text-sm mt-1">{errors['businessGoals']}</p>
                        )}
                      </div>
                      
                      <div className="mb-8">
                        <label className="block text-gray-700 mb-2">
                          Team Size*
                        </label>
                        <div className="grid md:grid-cols-2 gap-3">
                          {[
                            { id: 'solo', label: 'Solo/Freelancer' },
                            { id: 'small', label: '2-10 employees' },
                            { id: 'medium', label: '11-50 employees' },
                            { id: 'large', label: '51+ employees' }
                          ].map((option) => (
                            <CustomRadio
                              key={option.id}
                              id={`teamSize-${option.id}`}
                              name="teamSize"
                              value={option.id}
                              checked={formData.teamSize === option.id}
                              onChange={handleChange}
                              label={option.label}
                            />
                          ))}
                        </div>
                        {errors['teamSize'] && (
                          <p className="text-red-500 text-sm mt-1">{errors['teamSize']}</p>
                        )}
                      </div>
                      
                      <div className="mb-8">
                        <label className="block text-gray-700 mb-2">
                          Current Technology Stack (Select all that apply)
                        </label>
                        <div className="grid md:grid-cols-3 gap-3">
                          {[
                            { id: 'crm', label: 'CRM (e.g., Salesforce, HubSpot)' },
                            { id: 'erp', label: 'ERP System' },
                            { id: 'ecommerce', label: 'E-commerce Platform' },
                            { id: 'analytics', label: 'Analytics Tools' },
                            { id: 'automation', label: 'Automation Tools' },
                            { id: 'collaboration', label: 'Collaboration Software' }
                          ].map((option) => (
                            <CustomCheckbox
                              key={option.id}
                              id={`tech-${option.id}`}
                              name="techStack"
                              value={option.id}
                              checked={formData.techStack.includes(option.id)}
                              onChange={handleCheckboxChange}
                              label={option.label}
                            />
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex justify-between mt-8">
                        <button
                          type="button"
                          onClick={goToPreviousStep}
                          className="flex items-center border border-[#FF7066] text-[#FF7066] px-6 py-3 rounded-lg hover:bg-[#FF7066]/5 transition-colors"
                        >
                          <svg className="mr-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={goToNextStep}
                          className="flex items-center bg-[#FF7066] text-white px-6 py-3 rounded-lg hover:bg-[#FF8A82] transition-colors"
                        >
                          Next Step
                          <ArrowRightIcon className="ml-2 w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Step 3: Final Details */}
                  {formStep === 2 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h3 className="text-xl font-bold text-primary mb-6">Almost there! Final details</h3>
                      
                      <div className="mb-8">
                        <label className="block text-gray-700 mb-2" htmlFor="painPoints">
                          What are your biggest business challenges or pain points right now?
                        </label>
                        <textarea
                          id="painPoints"
                          name="painPoints"
                          value={formData.painPoints}
                          onChange={handleChange}
                          placeholder="e.g., Time-consuming manual processes, customer response delays, data analysis challenges..."
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF7066] h-24 transition-all"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Your specific challenges help us identify the best AI solutions for your business.
                        </p>
                      </div>
                      
                      <div className="mb-8">
                        <label className="block text-gray-700 mb-2" htmlFor="competitors">
                          Do you know of any competitors using AI? If so, how?
                        </label>
                        <input
                          type="text"
                          id="competitors"
                          name="competitors"
                          value={formData.competitors}
                          onChange={handleChange}
                          placeholder="e.g., Competitor X is using AI for customer service chatbots"
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF7066] transition-all"
                        />
                      </div>
                      
                      <div className="mb-8">
                        <label className="block text-gray-700 mb-4">
                          Implementation Timeline Priority
                        </label>
                        <div className="grid md:grid-cols-3 gap-3">
                          {[
                            { id: 'immediate', label: 'Immediate (1-3 months)' },
                            { id: 'medium', label: 'Medium-term (3-6 months)' },
                            { id: 'long', label: 'Long-term planning (6+ months)' }
                          ].map((option) => (
                            <CustomRadio
                              key={option.id}
                              id={`priority-${option.id}`}
                              name="implementationPriority"
                              value={option.id}
                              checked={formData.implementationPriority === option.id}
                              onChange={handleChange}
                              label={option.label}
                            />
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex justify-between mt-8">
                        <button
                          type="button"
                          onClick={goToPreviousStep}
                          className="flex items-center border border-[#FF7066] text-[#FF7066] px-6 py-3 rounded-lg hover:bg-[#FF7066]/5 transition-colors"
                        >
                          <svg className="mr-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={handleFinalSubmit}
                          disabled={isSubmitting}
                          className={`flex items-center bg-[#FF7066] text-white px-6 py-3 rounded-lg hover:bg-[#FF8A82] transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                          {isSubmitting ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Generating Your Audit...
                            </span>
                          ) : (
                            <>
                              Submit & Get Your Free Audit
                              <ArrowRightIcon className="ml-2 w-4 h-4" />
                            </>
                          )}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </motion.div>
            
            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-12 text-center flex flex-wrap justify-center gap-6"
            >
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-[#FF7066]" />
                <span className="text-secondary ml-2">100% Free Analysis</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-[#FF7066]" />
                <span className="text-secondary ml-2">Secure & Confidential</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-[#FF7066]" />
                <span className="text-secondary ml-2">Results in Minutes</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-[#FF7066]" />
                <span className="text-secondary ml-2">No Obligation</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}